import type { ReactNode } from 'react'
import { useSearchParams } from 'react-router'
import { ApiContractError, ApiError } from '@/api/client'
import type {
  ProducttypeFunnelSortBy,
  SortDirection,
  TopicCategory,
  TopicStatus,
} from '@/api/topics/types'
import { FunnelAreaNav } from '@/features/funnels/FunnelAreaNav'
import { ProducttypeFunnelCard } from '@/features/topics/ProducttypeFunnelCard'
import {
  useProducttypeFunnels,
  useProducttypeStatistics,
  useTopicActions,
} from '@/features/topics/useTopics'
import { formatNumber } from '@/lib/format'

const topicStatuses: readonly TopicStatus[] = ['Open', 'Approved', 'Ignored']
const topicCategories: readonly TopicCategory[] = [
  'Recipient',
  'Motif',
  'Hobby',
  'Trait',
  'Material',
  'Occasion',
]

const statusOptions: readonly { value: TopicStatus; label: string }[] = [
  { value: 'Open', label: 'Offen' },
  { value: 'Approved', label: 'Freigegeben' },
  { value: 'Ignored', label: 'Ignoriert' },
]

const categoryOptions: readonly { value: TopicCategory; label: string }[] = [
  { value: 'Recipient', label: 'Empfänger' },
  { value: 'Motif', label: 'Motiv' },
  { value: 'Hobby', label: 'Hobby' },
  { value: 'Trait', label: 'Eigenschaft' },
  { value: 'Material', label: 'Material' },
  { value: 'Occasion', label: 'Anlass' },
]

const sortOptions: readonly { value: ProducttypeFunnelSortBy; label: string }[] = [
  { value: 'OpenPotential', label: 'Offenes Potenzial' },
  { value: 'TotalPotential', label: 'Gesamtpotenzial' },
  { value: 'TopicCount', label: 'Anzahl Themen' },
  { value: 'ProducttypeName', label: 'Produkttyp' },
]

const PAGE_SIZE = 30

function isTopicStatus(value: string): value is TopicStatus {
  return topicStatuses.includes(value as TopicStatus)
}

function isTopicCategory(value: string): value is TopicCategory {
  return topicCategories.includes(value as TopicCategory)
}

function isFunnelSort(value: string): value is ProducttypeFunnelSortBy {
  return sortOptions.some((option) => option.value === value)
}

function nonNegativeNumber(value: string | null): number {
  if (value === null || value.trim() === '') return 0
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0
}

function positiveInteger(value: string | null, fallback: number): number {
  if (value === null) return fallback
  const parsed = Number(value)
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback
}

export function TopicsPage(): ReactNode {
  const [searchParams, setSearchParams] = useSearchParams()
  const statusValues = searchParams.getAll('status')
  const validStatuses = statusValues.filter(isTopicStatus)
  const statuses: readonly TopicStatus[] = statusValues.includes('none')
    ? []
    : validStatuses.length > 0
      ? validStatuses
      : topicStatuses
  const categoryValue = searchParams.get('category') ?? ''
  const category = isTopicCategory(categoryValue) ? categoryValue : ''
  const search = (searchParams.get('search') ?? '').trim()
  const minimumPotential = nonNegativeNumber(searchParams.get('minPotential'))
  const withinRankingCap = searchParams.get('withinRankingCap') === 'true'
  const sortValue = searchParams.get('sortBy') ?? ''
  const sortBy = isFunnelSort(sortValue) ? sortValue : 'OpenPotential'
  const sortDirection: SortDirection =
    searchParams.get('sortDirection') === 'Ascending' ? 'Ascending' : 'Descending'
  const page = positiveInteger(searchParams.get('page'), 1)

  const updateParams = (update: (params: URLSearchParams) => void, replace = false): void => {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current)
        update(next)
        return next
      },
      { replace },
    )
  }

  const funnelQuery = {
    status: statuses,
    ...(category !== '' ? { category } : {}),
    ...(search !== '' ? { search } : {}),
    withinRankingCap,
    ...(minimumPotential > 0 ? { minPotential: minimumPotential } : {}),
    sortBy,
    sortDirection,
    page,
    pageSize: PAGE_SIZE,
  }

  const { data, isLoading, error, refetch } = useProducttypeFunnels(funnelQuery)
  const { data: statistics } = useProducttypeStatistics({
    status: statuses,
    ...(category !== '' ? { category } : {}),
    withinRankingCap,
  })
  const actions = useTopicActions()

  const totalPages = data ? Math.max(1, Math.ceil(data.totalCount / data.pageSize)) : 1

  return (
    <div className="mx-auto max-w-6xl px-4 py-8">
      <header className="mb-6">
        <p className="text-sm font-semibold text-sky-700 dark:text-sky-300">
          Amazon T3 Keyword-Potenziale
        </p>
        <h1 className="mt-1 text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
          Potenzial-Trichter
        </h1>
        <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
          Produkttypen bündeln die Hauptrichter, Themen bilden die Sub-Trichter. Der Traffic
          beschreibt potenzielle monatliche Suchnachfrage, keine Umsatz- oder Conversion-Prognose.
        </p>
      </header>

      <FunnelAreaNav />

      <section
        aria-label="Trichter-Kennzahlen"
        className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4"
      >
        {[
          { label: 'Produkttypen (Treffer)', value: data?.totalCount ?? 0 },
          { label: 'Offene Themen', value: statistics?.statusCounts.open ?? 0 },
          { label: 'Bekanntes Potenzial', value: statistics?.knownPotentialSum ?? 0 },
          { label: 'Freigegebene Themen', value: statistics?.statusCounts.approved ?? 0 },
        ].map((metric) => (
          <div
            key={metric.label}
            className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800"
          >
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">{metric.label}</p>
            <p className="mt-1 text-2xl font-bold tabular-nums text-slate-900 dark:text-slate-100">
              {formatNumber(metric.value)}
            </p>
          </div>
        ))}
      </section>

      <p className="mb-4 text-xs text-slate-500 dark:text-slate-400">
        Die Kennzahlen oben gelten für Status- und Kategoriefilter, unabhängig von Suche und
        Mindestpotenzial. Die Liste unten berücksichtigt alle gesetzten Filter.
      </p>

      <div className="mb-6 flex flex-wrap items-end gap-4 rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
        <fieldset>
          <legend className="mb-1.5 text-xs font-medium text-slate-600 dark:text-slate-400">
            Themenstatus
          </legend>
          <div className="flex flex-wrap gap-3">
            {statusOptions.map((option) => (
              <label
                key={option.value}
                className="flex cursor-pointer items-center gap-1.5 text-sm text-slate-700 dark:text-slate-200"
              >
                <input
                  type="checkbox"
                  checked={statuses.includes(option.value)}
                  onChange={() => {
                    const nextStatuses = statuses.includes(option.value)
                      ? statuses.filter((item) => item !== option.value)
                      : [...statuses, option.value]
                    updateParams((params) => {
                      params.delete('status')
                      if (nextStatuses.length === 0) {
                        params.append('status', 'none')
                      } else if (nextStatuses.length !== topicStatuses.length) {
                        for (const item of nextStatuses) params.append('status', item)
                      }
                      params.delete('page')
                    })
                  }}
                  className="rounded border-slate-300 text-sky-700 focus:ring-sky-500"
                />
                {option.label}
              </label>
            ))}
          </div>
        </fieldset>

        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-slate-600 dark:text-slate-400">Suche</span>
          <input
            type="search"
            value={searchParams.get('search') ?? ''}
            onChange={(event) => {
              updateParams((params) => {
                const value = event.target.value
                if (value.trim() === '') params.delete('search')
                else params.set('search', value)
                params.delete('page')
              }, true)
            }}
            placeholder="Produkttyp oder Thema"
            className="w-64 rounded border border-slate-300 px-3 py-1.5 text-sm focus:border-sky-500 focus:outline-none dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
          />
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-slate-600 dark:text-slate-400">Kategorie</span>
          <select
            value={category}
            onChange={(event) => {
              updateParams((params) => {
                const value = event.target.value
                if (isTopicCategory(value)) params.set('category', value)
                else params.delete('category')
                params.delete('page')
              })
            }}
            className="rounded border border-slate-300 px-3 py-1.5 text-sm focus:border-sky-500 focus:outline-none dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
          >
            <option value="">Alle</option>
            {categoryOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-slate-600 dark:text-slate-400">
            Mindestpotenzial
          </span>
          <input
            type="number"
            min="0"
            step="100"
            value={minimumPotential === 0 ? '' : minimumPotential}
            onChange={(event) => {
              updateParams((params) => {
                const value = event.target.value
                if (value === '' || nonNegativeNumber(value) === 0) params.delete('minPotential')
                else params.set('minPotential', value)
                params.delete('page')
              }, true)
            }}
            placeholder="0"
            className="w-32 rounded border border-slate-300 px-3 py-1.5 text-sm focus:border-sky-500 focus:outline-none dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
          />
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-slate-600 dark:text-slate-400">Sortierung</span>
          <select
            value={sortBy}
            onChange={(event) => {
              updateParams((params) => {
                const value = event.target.value
                if (isFunnelSort(value)) params.set('sortBy', value)
                params.delete('page')
              })
            }}
            className="rounded border border-slate-300 px-3 py-1.5 text-sm focus:border-sky-500 focus:outline-none dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
          >
            {sortOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        <button
          type="button"
          onClick={() => {
            updateParams((params) => {
              params.set(
                'sortDirection',
                sortDirection === 'Descending' ? 'Ascending' : 'Descending',
              )
              params.delete('page')
            })
          }}
          className="rounded border border-slate-300 px-3 py-1.5 text-sm text-slate-700 transition hover:bg-slate-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700"
        >
          {sortDirection === 'Descending' ? 'Absteigend' : 'Aufsteigend'}
        </button>

        <label className="flex cursor-pointer items-center gap-1.5 pb-1.5 text-sm text-slate-700 dark:text-slate-200">
          <input
            type="checkbox"
            checked={withinRankingCap}
            onChange={(event) => {
              updateParams((params) => {
                if (event.target.checked) params.set('withinRankingCap', 'true')
                else params.delete('withinRankingCap')
                params.delete('page')
              })
            }}
            className="rounded border-slate-300 text-sky-700 focus:ring-sky-500"
          />
          Nur im Ranking-Fenster
        </label>
      </div>

      {actions.lastDecision?.nicheOrphaned === true && (
        <p className="mb-4 rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-100">
          Die Nische <strong>{actions.lastDecision.nicheName}</strong> hängt jetzt an keinem Thema
          mehr. Prüfe, ob sie noch gebraucht wird.
        </p>
      )}

      {actions.lastDecision?.nicheCreated === true && (
        <p className="mb-4 rounded border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-100">
          Neue Nische angelegt: <strong>{actions.lastDecision.nicheName}</strong>
        </p>
      )}

      {actions.error && (
        <p className="mb-4 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-100">
          {actions.error.message}
        </p>
      )}

      {isLoading && (
        <p className="py-12 text-center text-sm text-slate-500 dark:text-slate-400">
          Potenzial-Trichter werden geladen…
        </p>
      )}

      {error && (
        <div className="rounded border border-red-200 bg-red-50 p-4 dark:border-red-900 dark:bg-red-950">
          <p className="text-sm font-medium text-red-900 dark:text-red-100">
            {error instanceof ApiError || error instanceof ApiContractError
              ? error.message
              : 'Die Potenzial-Trichter konnten nicht geladen werden.'}
          </p>
          <button
            type="button"
            onClick={() => {
              void refetch()
            }}
            className="mt-3 rounded bg-red-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-red-700"
          >
            Erneut versuchen
          </button>
        </div>
      )}

      {data && !error && (
        <>
          <p className="mb-3 text-sm text-slate-600 dark:text-slate-400">
            {formatNumber(data.totalCount)} Hauptrichter, Seite {formatNumber(data.page)} von{' '}
            {formatNumber(totalPages)}
          </p>
          {data.items.length === 0 ? (
            <p className="rounded-xl border border-slate-200 bg-white py-12 text-center text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400">
              Keine Potenzial-Trichter für diese Auswahl.
            </p>
          ) : (
            <>
              <div className="space-y-4">
                {data.items.map((funnel) => (
                  <ProducttypeFunnelCard
                    key={funnel.producttypeKey}
                    funnel={funnel}
                    topicFilters={{ status: statuses, withinRankingCap }}
                    defaultExpanded={search !== '' && data.items.length <= 10}
                    pendingTopicId={actions.pendingTopicId}
                    onApprove={actions.approve}
                    onIgnore={actions.ignore}
                    onReset={actions.reset}
                  />
                ))}
              </div>

              {totalPages > 1 && (
                <div className="mt-4 flex items-center justify-between">
                  <button
                    type="button"
                    disabled={page <= 1}
                    onClick={() => {
                      updateParams((params) => {
                        params.set('page', String(page - 1))
                      })
                    }}
                    className="rounded border border-slate-300 px-3 py-1.5 text-sm text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700"
                  >
                    Zurück
                  </button>
                  <button
                    type="button"
                    disabled={page >= totalPages}
                    onClick={() => {
                      updateParams((params) => {
                        params.set('page', String(page + 1))
                      })
                    }}
                    className="rounded border border-slate-300 px-3 py-1.5 text-sm text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700"
                  >
                    Weiter
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  )
}
